<!--
  "659 Erlebnisse in ➤ Köln" under the homepage hero search (KFT-4205). A plain
  link, lighter than the search field: the city opens the picker ("Überall"
  searches everywhere); the arrow marks the IP city. A city named in the query
  ("töpfern hamburg") shows instead, for that search only. Renders nothing until the client resolves the
  city (useSearchCity) — the row keeps its height so nothing shifts.
-->
<template>
  <div
    ref="rootRef"
    class="relative flex min-h-6 flex-wrap items-center gap-x-1.5 text-sm leading-6 text-stone-600"
    data-test-id="search-city-line"
  >
    <template v-if="isResolved">
      <template v-if="shownCity">
        <span>{{
          shownCity.eventCount == null
            ? t('search.searchCity.searchIn')
            : t(
                'search.searchCity.experiencesIn',
                { count: shownCity.eventCount.toLocaleString(locale) },
                shownCity.eventCount
              )
        }}</span>
        <button
          ref="triggerRef"
          type="button"
          class="city-link"
          :class="{ 'city-link--open': isOpen }"
          aria-haspopup="dialog"
          :aria-expanded="isOpen"
          :aria-label="
            t('search.searchCity.changeAria', { city: cityName(shownCity) })
          "
          data-test-id="search-city-trigger"
          @click="toggle"
        >
          <IconNavigation
            v-if="!queryCity && source === 'ip'"
            :size="13"
            :stroke-width="2.2"
            aria-hidden="true"
          />
          {{ cityName(shownCity) }}
          <IconChevronDown
            :size="14"
            :stroke-width="2.2"
            class="transition-transform duration-150"
            :class="{ 'rotate-180': isOpen }"
            aria-hidden="true"
          />
        </button>
        <small v-if="queryCity" class="text-xs leading-6 text-primary">{{
          t('search.searchCity.fromQuery')
        }}</small>
      </template>
      <button
        v-else
        ref="triggerRef"
        type="button"
        class="city-link"
        :class="{ 'city-link--open': isOpen }"
        aria-haspopup="dialog"
        :aria-expanded="isOpen"
        data-test-id="search-city-trigger"
        @click="toggle"
      >
        {{ t('search.searchCity.everywhere') }}
        <IconChevronDown
          :size="14"
          :stroke-width="2.2"
          class="transition-transform duration-150"
          :class="{ 'rotate-180': isOpen }"
          aria-hidden="true"
        />
      </button>
    </template>

    <Teleport to="body" :disabled="!isMobile">
      <SearchCityPicker
        v-if="isOpen"
        :variant="isMobile ? 'sheet' : 'popover'"
        :analytics-placement="analyticsPlacement"
        @close="close"
      />
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, nextTick, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { onClickOutside } from '@vueuse/core'
import {
  ChevronDown as IconChevronDown,
  Navigation as IconNavigation,
} from 'lucide-vue-next'
import { useDevice } from '#imports'
import { useSearchCity } from '~/composables/search/useSearchCity'
import { resolveLocalizedName } from '~/utils/resolveLocalizedName'
import { useSearchContentGroup } from '~/composables/search/useSearchContentGroup'
import { buildSearchCityParams } from '~/utils/searchAnalytics'
import type { FederatedSearchAnalyticsPlacement } from '~/utils/searchEngineHelpers'
import type { SearchCity } from '~/utils/searchCity'

// The picker (and its Typesense client) only loads when first opened.
const SearchCityPicker = defineAsyncComponent(
  () => import('./SearchCityPicker.vue')
)

const { queryCity = null, analyticsPlacement = undefined } = defineProps<{
  queryCity?: SearchCity | null
  analyticsPlacement?: FederatedSearchAnalyticsPlacement
}>()
const emit = defineEmits<{ open: [] }>()

const { t, locale } = useI18n()
const { isMobile } = useDevice()
const { city, source, isResolved, init } = useSearchCity()

const rootRef = ref<HTMLElement | null>(null)
const triggerRef = ref<HTMLElement | null>(null)
const isOpen = ref(false)

const shownCity = computed(() => queryCity ?? city.value)
const cityName = (c: SearchCity) => resolveLocalizedName(c.name, locale.value)

const close = async () => {
  isOpen.value = false
  await nextTick()
  triggerRef.value?.focus()
}

const contentGroup = useSearchContentGroup()

// GA4 `search_city_picker_open` (KFT-4280). The GA helper is loaded on
// demand: this line is in the hero's initial bundle.
const trackPickerOpen = async () => {
  try {
    const { sendSearchCityPickerOpen } =
      await import('~/composables/useGtmEvents')
    sendSearchCityPickerOpen({
      search_placement: analyticsPlacement ?? '',
      // The line only shows on the hero, so it always scopes the search
      ...buildSearchCityParams({
        withCity: true,
        city: city.value,
        source: source.value,
        queryCity,
      }),
      content_group: contentGroup(),
    })
  } catch (error) {
    console.error('[SearchCityLine] picker open tracking failed', error)
  }
}

const toggle = () => {
  isOpen.value = !isOpen.value
  // The search panel and the picker never show together.
  if (isOpen.value) {
    emit('open')
    trackPickerOpen()
  }
}

// Popover only: the sheet covers the screen and closes via back / a pick.
onClickOutside(rootRef, () => {
  if (!isMobile) isOpen.value = false
})

onMounted(init)
</script>

<style lang="css" scoped>
@reference '~/assets/css/tailwind.css';

.city-link {
  @apply inline-flex items-center gap-1 rounded font-semibold text-primary underline decoration-[1.5px] underline-offset-4;
  text-decoration-color: color-mix(
    in srgb,
    var(--color-primary) 40%,
    transparent
  );

  &:hover,
  &--open {
    text-decoration-color: var(--color-primary);
  }

  &:focus-visible {
    @apply outline-2 outline-offset-2 outline-primary;
  }
}
</style>
